import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowsRotate,
  faPause,
  faPlay,
  faXmark,
  faCircleCheck,
  faCircleXmark,
  faExclamationCircle,
  faClock,
  faFile,
  faRotateRight,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';
import { toast, swalConfirm } from '@/lib/swal';
import {
  listMigrations,
  pauseMigration,
  resumeMigration,
  cancelMigration,
  getMigration,
  type MigrationDto,
} from './api/platform-storage.api';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtSeconds(sec: number | null): string {
  if (sec === null || sec <= 0) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

const STATUS_CFG = {
  PENDING: { icon: faClock, color: 'text-muted-foreground', bg: 'bg-muted', label: 'Pending' },
  RUNNING: { icon: faArrowsRotate, color: 'text-primary', bg: 'bg-primary/10', label: 'Running' },
  PAUSED: { icon: faPause, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Paused' },
  COMPLETED: {
    icon: faCircleCheck,
    color: 'text-green-600 dark:text-green-400',
    bg: 'bg-green-500/10',
    label: 'Completed',
  },
  FAILED: {
    icon: faCircleXmark,
    color: 'text-destructive',
    bg: 'bg-destructive/10',
    label: 'Failed',
  },
  ROLLED_BACK: {
    icon: faExclamationCircle,
    color: 'text-amber-500',
    bg: 'bg-amber-500/10',
    label: 'Rolled Back',
  },
} as const;

type StatusKey = keyof typeof STATUS_CFG;

function statusCfg(s: string) {
  return STATUS_CFG[(s as StatusKey) in STATUS_CFG ? (s as StatusKey) : 'PENDING'];
}

const ACTIVE_STATUSES = new Set(['RUNNING', 'PAUSED', 'PENDING']);

// ── Progress bar ──────────────────────────────────────────────────────────────

function ProgressBar({ percent, status }: { percent: number; status: string }) {
  return (
    <div className="h-3 w-full overflow-hidden rounded-full bg-muted">
      <div
        className={cn(
          'h-full rounded-full transition-all duration-500',
          status === 'COMPLETED'
            ? 'bg-green-500'
            : status === 'FAILED' || status === 'ROLLED_BACK'
              ? 'bg-destructive'
              : status === 'PAUSED'
                ? 'bg-amber-500'
                : 'bg-primary',
        )}
        style={{ width: `${Math.min(percent, 100)}%` }}
      />
    </div>
  );
}

// ── Active Migration Panel ────────────────────────────────────────────────────

interface ActivePanelProps {
  migration: MigrationDto;
  onAction: () => void;
}

function ActiveMigrationPanel({ migration, onAction }: ActivePanelProps) {
  const [acting, setActing] = useState<'pause' | 'resume' | 'cancel' | null>(null);
  const cfg = statusCfg(migration.status);

  const handlePause = async () => {
    setActing('pause');
    try {
      await pauseMigration(migration.id);
      toast.success('Migration paused');
      onAction();
    } catch (e: unknown) {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to pause',
      );
    } finally {
      setActing(null);
    }
  };

  const handleResume = async () => {
    setActing('resume');
    try {
      await resumeMigration(migration.id);
      toast.success('Migration resumed');
      onAction();
    } catch (e: unknown) {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to resume',
      );
    } finally {
      setActing(null);
    }
  };

  const handleCancel = async () => {
    const ok = await swalConfirm({
      title: 'Cancel Migration?',
      text: 'Partially copied files will be deleted. The original files will remain untouched.',
      confirmText: 'Cancel Migration',
    });
    if (!ok) return;
    setActing('cancel');
    try {
      await cancelMigration(migration.id);
      toast.success('Migration cancelled and rolled back');
      onAction();
    } catch (e: unknown) {
      toast.error(
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Failed to cancel',
      );
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      {/* Status header */}
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div
            className={cn('flex h-10 w-10 shrink-0 items-center justify-center rounded-lg', cfg.bg)}
          >
            <FontAwesomeIcon
              icon={cfg.icon}
              className={cn('h-5 w-5', cfg.color, migration.status === 'RUNNING' && 'animate-spin')}
            />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">File Migration</p>
            <p className={cn('text-xs font-medium', cfg.color)}>{cfg.label}</p>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          {migration.status === 'RUNNING' && (
            <button
              onClick={() => void handlePause()}
              disabled={acting !== null}
              className="flex items-center gap-1.5 rounded-lg border border-amber-300 bg-amber-500/5 px-3 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 transition-colors hover:bg-amber-500/10 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPause} className="h-3 w-3" />
              Pause
            </button>
          )}
          {migration.status === 'PAUSED' && (
            <button
              onClick={() => void handleResume()}
              disabled={acting !== null}
              className="flex items-center gap-1.5 rounded-lg border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faPlay} className="h-3 w-3" />
              Resume
            </button>
          )}
          {ACTIVE_STATUSES.has(migration.status) && (
            <button
              onClick={() => void handleCancel()}
              disabled={acting !== null}
              className="flex items-center gap-1.5 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-1.5 text-xs font-medium text-destructive transition-colors hover:bg-destructive/10 disabled:opacity-50"
            >
              <FontAwesomeIcon icon={faXmark} className="h-3 w-3" />
              Cancel
            </button>
          )}
        </div>
      </div>

      {/* Path info */}
      <div className="mb-4 rounded-lg bg-muted/50 p-3 space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="w-14 shrink-0 text-xs text-muted-foreground">From</span>
          <span className="font-mono text-xs text-foreground break-all">
            {migration.sourcePath || '—'}
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="w-14 shrink-0 text-xs text-muted-foreground">To</span>
          <span className="font-mono text-xs text-foreground break-all">
            {migration.destPath || '—'}
          </span>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <ProgressBar percent={migration.percentComplete} status={migration.status} />
      </div>

      {/* Stats row */}
      <div className="mb-3 flex flex-wrap items-center gap-4 text-sm">
        <span className="font-semibold text-foreground">{migration.percentComplete}%</span>
        <span className="text-muted-foreground">
          {migration.migratedObjects.toLocaleString()} / {migration.totalObjects.toLocaleString()}{' '}
          files
        </span>
        {migration.failedObjects > 0 && (
          <span className="text-destructive">{migration.failedObjects} failed</span>
        )}
        {migration.estimatedRemainingSeconds !== null && migration.status === 'RUNNING' && (
          <span className="text-muted-foreground">
            ETA: {fmtSeconds(migration.estimatedRemainingSeconds)}
          </span>
        )}
      </div>

      {/* Current file */}
      {migration.currentFile && migration.status === 'RUNNING' && (
        <div className="flex items-center gap-2 rounded-lg bg-muted/50 px-3 py-2">
          <FontAwesomeIcon icon={faFile} className="h-3 w-3 shrink-0 text-muted-foreground" />
          <span className="truncate font-mono text-xs text-muted-foreground">
            {migration.currentFile}
          </span>
        </div>
      )}

      {/* Timestamps */}
      <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
        {migration.startedAt && (
          <span className="flex items-center gap-1">
            <FontAwesomeIcon icon={faClock} className="h-3 w-3" />
            Started {new Date(migration.startedAt).toLocaleString()}
          </span>
        )}
        {migration.completedAt && (
          <span>Completed {new Date(migration.completedAt).toLocaleString()}</span>
        )}
        {migration.pausedAt && migration.status === 'PAUSED' && (
          <span>Paused {new Date(migration.pausedAt).toLocaleString()}</span>
        )}
      </div>
    </div>
  );
}

// ── History Row ───────────────────────────────────────────────────────────────

function HistoryRow({ migration }: { migration: MigrationDto }) {
  const cfg = statusCfg(migration.status);
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-4">
      <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-lg', cfg.bg)}>
        <FontAwesomeIcon icon={cfg.icon} className={cn('h-4 w-4', cfg.color)} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={cn('text-xs font-semibold', cfg.color)}>{cfg.label}</span>
          <span className="text-xs text-muted-foreground">
            {migration.migratedObjects.toLocaleString()} / {migration.totalObjects.toLocaleString()}{' '}
            files
          </span>
          {migration.failedObjects > 0 && (
            <span className="text-xs text-destructive">· {migration.failedObjects} failed</span>
          )}
        </div>
        <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="font-mono truncate max-w-[200px]">{migration.sourcePath}</span>
          <span>→</span>
          <span className="font-mono truncate max-w-[200px]">{migration.destPath}</span>
        </div>
      </div>
      <div className="shrink-0 text-xs text-muted-foreground">
        {migration.completedAt
          ? new Date(migration.completedAt).toLocaleDateString()
          : migration.startedAt
            ? new Date(migration.startedAt).toLocaleDateString()
            : new Date(migration.createdAt).toLocaleDateString()}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function MigrationCenterPage() {
  const [migrations, setMigrations] = useState<MigrationDto[]>([]);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const activeMigration = migrations.find((m) => ACTIVE_STATUSES.has(m.status)) ?? null;
  const history = migrations.filter((m) => !ACTIVE_STATUSES.has(m.status));

  const load = useCallback(async () => {
    try {
      const list = await listMigrations();
      setMigrations(Array.isArray(list) ? list : []);
    } catch {
      // fail silently on poll
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll for active migration progress
  const refreshActive = useCallback(async () => {
    if (!activeMigration) return;
    try {
      const fresh = await getMigration(activeMigration.id);
      setMigrations((prev) => prev.map((m) => (m.id === fresh.id ? fresh : m)));
    } catch {
      // non-fatal
    }
  }, [activeMigration]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (activeMigration?.status === 'RUNNING') {
      pollRef.current = setInterval(() => {
        void refreshActive();
      }, 2000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [activeMigration?.status, refreshActive]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground">Migration Center</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Track and manage storage provider file migrations
          </p>
        </div>
        <button
          onClick={() => void load()}
          disabled={loading}
          className={cn(
            'flex items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted',
            loading && 'opacity-50 cursor-not-allowed',
          )}
        >
          <FontAwesomeIcon
            icon={faRotateRight}
            className={cn('h-3.5 w-3.5', loading && 'animate-spin')}
          />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">
          <div className="h-48 animate-pulse rounded-2xl bg-muted" />
          <div className="h-16 animate-pulse rounded-xl bg-muted" />
        </div>
      ) : activeMigration ? (
        <>
          <ActiveMigrationPanel migration={activeMigration} onAction={() => void load()} />
          {history.length > 0 && (
            <div>
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Migration History
              </p>
              <div className="space-y-2">
                {history.map((m) => (
                  <HistoryRow key={m.id} migration={m} />
                ))}
              </div>
            </div>
          )}
        </>
      ) : migrations.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card py-16 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="h-7 w-7 text-green-600 dark:text-green-400"
            />
          </div>
          <h2 className="mt-4 text-sm font-semibold text-foreground">No migrations</h2>
          <p className="mt-2 max-w-sm text-xs text-muted-foreground">
            Migrations are started automatically when you change the Root Path of an active storage
            provider from the Providers page.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col items-center justify-center rounded-2xl border border-border bg-card py-10 text-center">
            <FontAwesomeIcon
              icon={faCheckCircle}
              className="h-8 w-8 text-green-600 dark:text-green-400 mb-3"
            />
            <p className="text-sm font-medium text-foreground">No active migrations</p>
            <p className="mt-1 text-xs text-muted-foreground">All migrations have completed</p>
          </div>
          <div>
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Migration History
            </p>
            <div className="space-y-2">
              {history.map((m) => (
                <HistoryRow key={m.id} migration={m} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
